/**
 * Cart week-status cleanup: on initial page load, clears the entire cart when any line item's
 * SSR data-product-custom-status is missing, empty, or not "deze week".
 *
 * Data comes from snippets/cart-products.liquid (cart drawer + /cart page). When DOM rows cannot
 * be matched 1:1 to /cart.js items (e.g. subscription templates without cart drawer), the check is skipped.
 */

import { CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const CONSOLE_PREFIX = '[CART-WEEK-STATUS-CLEANUP]';
const DEZE_WEEK_STATUS = 'deze week';
const CART_LINE_SELECTOR = '[data-cart-line-key][data-product-custom-status]';

const CART_CLEAR_URL =
  typeof Theme !== 'undefined' && Theme?.routes?.cart_url
    ? `${String(Theme.routes.cart_url).replace(/\/?$/, '')}/clear.js`
    : '/cart/clear.js';

/** @type {number} */
let lastKnownCartItemCount = 0;
let cleanupScheduled = false;
let cleanupInFlight = false;

/**
 * @param {'log' | 'warn' | 'error'} level
 * @param {string} message
 * @param {...any} args
 */
function logMessage(level, message, ...args) {
  // /** @type {any} */ (console)[level](`${CONSOLE_PREFIX} ${message}`, ...args);
}

function syncCartItemCountHintFromDom() {
  const n = Number.parseInt(String(document.body?.dataset?.tvCartLineCount ?? '0'), 10);
  lastKnownCartItemCount = Number.isFinite(n) && n >= 0 ? n : 0;
}

/** @returns {string} */
function getCartJsonUrl() {
  if (typeof Theme !== 'undefined' && Theme?.routes?.cart_url) {
    return `${String(Theme.routes.cart_url).replace(/\/?$/, '')}.js`;
  }
  return '/cart.js';
}

/**
 * @returns {Map<string, string>} Line key → normalized product_custom_status from DOM
 */
function buildLineKeyToStatusMap() {
  /** @type {Map<string, string>} */
  const map = new Map();
  for (const el of document.querySelectorAll(CART_LINE_SELECTOR)) {
    if (!(el instanceof HTMLElement)) continue;
    const key = el.dataset.cartLineKey;
    if (!key || map.has(key)) continue;
    map.set(key, String(el.dataset.productCustomStatus ?? '').trim().toLowerCase());
  }
  return map;
}

/**
 * @param {any} cartData
 * @returns {boolean} True when the cart should be cleared due to week status
 */
function shouldClearCartForWeekStatus(cartData) {
  const items = /** @type {any} */ (cartData)?.items;
  if (!Array.isArray(items) || items.length === 0) {
    return false;
  }

  const lineKeyToStatus = buildLineKeyToStatusMap();

  if (lineKeyToStatus.size === 0) {
    logMessage('log', 'No cart line week-status attributes in DOM; skipping check');
    return false;
  }

  if (lineKeyToStatus.size !== items.length) {
    logMessage(
      'log',
      `DOM line rows (${lineKeyToStatus.size}) do not match cart items (${items.length}); skipping check`
    );
    return false;
  }

  for (const item of items) {
    const key = /** @type {any} */ (item)?.key;
    if (key == null) {
      logMessage('log', 'Cart line missing key; skipping check');
      return false;
    }

    const status = lineKeyToStatus.get(String(key));
    if (status === undefined) {
      logMessage('log', `No DOM status for cart line key ${String(key)}; skipping check`);
      return false;
    }

    if (status !== DEZE_WEEK_STATUS) {
      logMessage('warn', `Cart line ${String(key)} status is not "${DEZE_WEEK_STATUS}": "${status}"`);
      return true;
    }
  }

  return false;
}

/**
 * Section IDs to re-render after a cart clear (aligned with component-cart-items updateQuantity).
 * @returns {Set<string>}
 */
function getCartClearSectionsToUpdate() {
  /** @type {Set<string>} */
  const sectionsToUpdate = new Set();

  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionsToUpdate.add(item.dataset.sectionId);
    }
  });

  const subscriptionModalSectionId = document
    .querySelector('[data-tv-subscription-cart-modal][data-section-id]')
    ?.getAttribute('data-section-id');
  if (subscriptionModalSectionId) {
    sectionsToUpdate.add(subscriptionModalSectionId);
  }

  const mainContent = document.getElementById('MainContent');
  if (mainContent) {
    const collectionStickyFooter = mainContent.querySelector('custom-collection-footer-component');
    if (collectionStickyFooter instanceof HTMLElement) {
      const collectionStickyFooterSectionId = collectionStickyFooter.getAttribute('data-section-id');
      if (collectionStickyFooterSectionId) {
        sectionsToUpdate.add(collectionStickyFooterSectionId);
      }
    }
  }

  const mealDeliveryMomentAppSection = document.querySelector('.meal-delivery-moment-app-section');
  if (mealDeliveryMomentAppSection instanceof HTMLElement) {
    const mealDeliveryMomentAppSectionId = mealDeliveryMomentAppSection.getAttribute('data-section-id');
    if (mealDeliveryMomentAppSectionId) {
      sectionsToUpdate.add(mealDeliveryMomentAppSectionId);
    }
  }

  const deliveryMomentSection = document.querySelector('delivery-moment-component');
  if (deliveryMomentSection instanceof HTMLElement) {
    const deliveryMomentSectionId = deliveryMomentSection
      .closest('[id^="shopify-section-"]')
      ?.id.replace('shopify-section-', '');
    if (deliveryMomentSectionId) {
      sectionsToUpdate.add(deliveryMomentSectionId);
    }
  }

  return sectionsToUpdate;
}

/** @returns {Promise<boolean>} */
async function clearCartAndRefreshSections() {
  try {
    logMessage('log', 'Clearing entire cart due to non-"deze week" line item(s)');

    const response = await fetch(CART_CLEAR_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      logMessage('error', 'Failed to clear cart:', response.status, response.statusText);
      return false;
    }

    logMessage('log', 'Successfully cleared cart');

    lastKnownCartItemCount = 0;
    if (document.body) {
      document.body.dataset.tvCartLineCount = '0';
    }

    const sectionsToUpdate = getCartClearSectionsToUpdate();

    await Promise.all(
      Array.from(sectionsToUpdate).map((sectionId) =>
        sectionRenderer.renderSection(sectionId, { cache: false }).catch((error) => {
          logMessage('warn', `Failed to render section ${sectionId}:`, error);
        })
      )
    );

    document.dispatchEvent(
      new CartUpdateEvent({}, 'thuysvers-cart-week-status-cleanup', {
        itemCount: 0,
        source: 'thuysvers-cart-week-status-cleanup',
      })
    );

    return true;
  } catch (error) {
    logMessage('error', 'Error clearing cart:', error);
    return false;
  }
}

/**
 * Fetch cart and clear it when any line fails the "deze week" DOM status check.
 * @returns {Promise<boolean>} True if a clear was attempted
 */
export async function enforceDezeWeekCartStatus() {
  if (cleanupInFlight) return false;
  if (lastKnownCartItemCount <= 0) return false;

  cleanupInFlight = true;

  try {
    const response = await fetch(getCartJsonUrl());
    if (!response.ok) return false;

    const cartData = await response.json();
    const items = Array.isArray(cartData?.items) ? cartData.items : [];

    if (items.length === 0) {
      lastKnownCartItemCount = 0;
      if (document.body) document.body.dataset.tvCartLineCount = '0';
      return false;
    }

    if (!shouldClearCartForWeekStatus(cartData)) {
      return false;
    }

    return await clearCartAndRefreshSections();
  } catch (error) {
    logMessage('error', 'Week-status cart enforcement failed:', error);
    return false;
  } finally {
    cleanupInFlight = false;
  }
}

function scheduleCleanup() {
  if (cleanupScheduled) return;
  cleanupScheduled = true;
  requestAnimationFrame(() => {
    cleanupScheduled = false;
    void enforceDezeWeekCartStatus();
  });
}

function shouldScheduleInitialCleanup() {
  return lastKnownCartItemCount > 0;
}

/** Idempotent bootstrap for initial page load (and bfcache restore). */
export function initCartWeekStatusCleanup() {
  if (/** @type {any} */ (window).__tvCartWeekStatusCleanupInit) return;
  /** @type {any} */ (window).__tvCartWeekStatusCleanupInit = true;

  syncCartItemCountHintFromDom();

  const runIfNeeded = () => {
    syncCartItemCountHintFromDom();
    if (shouldScheduleInitialCleanup()) scheduleCleanup();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runIfNeeded, { once: true });
  } else {
    runIfNeeded();
  }

  window.addEventListener('pageshow', (e) => {
    syncCartItemCountHintFromDom();
    if (e.persisted && shouldScheduleInitialCleanup()) scheduleCleanup();
  });
}

initCartWeekStatusCleanup();
